"use client";

import { usePathname } from "next/navigation";
import { useGlobalComponentData } from "@/lib/storyblok/hooks/useGlobalComponentData";
import type { Article } from "@/generated/sb";
import React from "react";
import {
  storyblokEditable,
  StoryblokServerComponent,
  StoryblokStory,
} from "@storyblok/react/rsc";
import { Main } from "@repo/ui-parkmobile/Main";
import { sectionResolver } from "@/lib/storyblok/adaptors";
import { groupSections } from "@/lib/groupSections";
import { extractArticleSchemaData } from "@/lib/article";
import { WebPageSchema, generateArticleEntitySchema } from "@/components/schema";

export interface ArticleProps {
  blok: Article;
}

export function Article({ blok }: ArticleProps) {
  const pathname = usePathname();
  const globalHeader = useGlobalComponentData(blok.header as string);
  const globalFooter = useGlobalComponentData(blok.footer as string);

  const baseUrl = process.env.NEXT_PUBLIC_DOMAIN || "";
  const pageUrl = `${baseUrl}${pathname}`;

  const articleSchemaData = extractArticleSchemaData(blok);

  const { sections, publicationDate } = blok;

  if (!sections) return <div>No sections</div>;

  const groupedSections = groupSections(sections);

  const articleEntity = articleSchemaData
    ? generateArticleEntitySchema(articleSchemaData)
    : null;

  return (
    <div className={`uipm:flex uipm:flex-col uipm:min-h-full`}>
      {articleSchemaData && (
        <WebPageSchema
          name={articleSchemaData.headline}
          pageUrl={pageUrl}
          description={articleSchemaData.description}
          mainEntity={articleEntity ?? undefined}
        />
      )}
      {globalHeader && (
        <StoryblokStory
          story={globalHeader}
          bridgeOptions={{
            resolveLinks: "url",
          }}
        />
      )}
      <Main {...storyblokEditable(blok)}>
        {groupedSections.map((group, groupIndex) => {
          return group.sections.map((section, sectionIndex) => {
            // Pass publicationDate to ArticleSection components
            const additionalProps =
              section.component === "articleSection" ? { publicationDate } : {};

            const heroArticle =
              section.component === "heroArticle" ? section : {};

            const level = (heroArticle as any).typography?.[0].typography?.[0]
              .level;

            // TODO: find how to override in storyblok
            // Hack
            // Override fontSize for h1 in heroArticle
            const sectionWithOverrides =
              section.component === "heroArticle" && level
                ? {
                    ...section,
                    typography: section.typography?.map((typo, index) =>
                      index === 0
                        ? {
                            ...typo,
                            typography: typo.typography?.map(
                              (item, itemIndex) =>
                                itemIndex === 0
                                  ? {
                                      ...item,
                                      level: "h1",
                                    }
                                  : item,
                            ),
                          }
                        : typo,
                    ),
                  }
                : section;

            return (
              <StoryblokServerComponent
                key={section._uid}
                blok={sectionWithOverrides}
                isHeroSection={groupIndex === 0 && sectionIndex === 0}
                pageType="article"
                {...storyblokEditable(section)}
                {...sectionResolver(section)}
                {...additionalProps}
              />
            );
          });
        })}
      </Main>
      {globalFooter && (
        <StoryblokStory
          story={globalFooter}
          bridgeOptions={{
            resolveLinks: "url",
          }}
        />
      )}
    </div>
  );
}
